import { getViewBADetails } from '@/core/service/service'
import {
    TLicensesResponseItem,
    TProfileResponse,
    TViewBADetails,
    viewBADetailsAtom,
    viewBADetailsErrorAtom,
    viewBADetailsIsLoadingAtom
} from '@/core/store/viewBA.store'
import { useCustomerInfo, useDistributorData } from '@/core/hooks/useStorage'
import { LoadingState, ErrorState, PageContainer } from '@/core/components'
import { Button, Paper, Stack, Typography } from '@mui/material'
import { useAtom } from 'jotai'
import { useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router'
import { formatDateDMY, getBaStatus, getFullRank } from '@/core/utils/utils'

const ViewBADetailsLayout = () => {
    const navigate = useNavigate()
    const [viewBADetails, setViewBADetails] = useAtom(viewBADetailsAtom)
    const [isLoading, setIsLoading] = useAtom(viewBADetailsIsLoadingAtom)
    const [errorMessage, setErrorMessage] = useAtom(viewBADetailsErrorAtom)

    const { baid: currentBaid, name: currentName } = useDistributorData()
    const { token: cstoken, href: customerHref } = useCustomerInfo()

    const handleGoBack = useCallback(() => {
        navigate('/mlm/distributor', { replace: true })
    }, [navigate])

    const fetchDetails = useCallback(async () => {
        if (!currentBaid || !cstoken || !customerHref) {
            setIsLoading(false)
            setErrorMessage('Không có dữ liệu')
            return
        }

        setIsLoading(true)
        setErrorMessage('')

        try {
            const [licensesData, profileData] = await Promise.all([
                getViewBADetails({ token: cstoken, href: customerHref, queryString: '/licenses' }),
                getViewBADetails({ token: cstoken, href: customerHref, queryString: '?expand=profilePicture,cumulativeMetricsProfile' })
            ])

            const licenses = licensesData.data.items[0] as TLicensesResponseItem
            const profile = profileData.data as TProfileResponse

            const addressOfResidence = [
                profile?.mainAddress?.address1,
                profile?.mainAddress?.address2,
                profile?.mainAddress?.city,
                profile?.mainAddress?.state,
                profile?.mainAddress?.zip,
                profile?.mainAddress?.country
            ]
                .filter(Boolean)
                .join(' ')

            const mergedData: TViewBADetails = {
                fullName: licenses?.humanName?.fullName ?? '-',
                distributorId: licenses?.id?.unicity ?? '-',
                birthDate: licenses?.birthDate ?? '-',
                taxId: licenses?.taxTerms?.taxId ?? '-',
                email: profile?.email ?? '-',
                phoneNumber: profile?.mobilePhone || profile?.homePhone || '-',
                AddressOfResidence: addressOfResidence || '-',
                dateOfJoin: profile?.joinDate ?? '-',
                permanentAddress: addressOfResidence || '-',
                status: getBaStatus(profile?.status.substring(0, 1) ?? ''),
                placeOfResidenceRegistration: '-',
                workPermitPhoneNumber: '-',
                bankName: licenses?.depositBankAccount?.bankName ?? '-',
                bankAccountNumber: licenses?.depositBankAccount?.accountNumber ?? '-',
                rank: licenses?.enroller?.highestRankShort ?? '-',
                sponsorId: profile?.sponsor?.id?.unicity ?? '-',
                nameOfSponsor: profile?.sponsor?.humanName?.fullName ?? '-',
                rankOfSponsor: profile?.cumulativeMetricsProfile?.highestRankShort ?? '-',
            }

            setViewBADetails(mergedData)
        } catch {
            setErrorMessage('Không thể lấy thông tin')
        } finally {
            setIsLoading(false)
        }
    }, [currentBaid, cstoken, customerHref, setErrorMessage, setIsLoading, setViewBADetails])

    useEffect(() => {
        if (currentBaid && cstoken && customerHref) {
            fetchDetails()
        }
    }, [currentBaid, cstoken, customerHref, fetchDetails])

    if (isLoading) {
        return <LoadingState />
    }

    if (errorMessage) {
        return <ErrorState message={errorMessage} onAction={handleGoBack} />
    }

    const rows = [
        {
            leftLabel: 'Họ Tên :',
            leftValue: viewBADetails.fullName,
            rightLabel: 'Mã số nhà phân phối/Mã số hợp đồng/\nMã KH thân thiết :',
            rightValue: viewBADetails.distributorId
        },
        {
            leftLabel: 'Ngày Sinh :',
            leftValue: formatDateDMY(viewBADetails.birthDate),
            rightLabel: 'Số CCCD/CMTND/Hộ Chiếu :',
            rightValue: viewBADetails.taxId
        },
        { 
            leftLabel: 'Email :', 
            leftValue: viewBADetails.email, 
            rightLabel: 'Số điện thoại :', 
            rightValue: viewBADetails.phoneNumber
        },
        {
            leftLabel: 'Địa chỉ cư trú :',
            leftValue: viewBADetails.AddressOfResidence,
            rightLabel: 'Ngày gia nhập :',
            rightValue: formatDateDMY(viewBADetails.dateOfJoin)
        },
        {
            leftLabel: 'Địa chỉ thường trú :',
            leftValue: viewBADetails.permanentAddress,
            rightLabel: 'Tình trạng hoạt động :',
            rightValue: viewBADetails.status
        },
        {
            leftLabel: 'Nơi đăng ký lưu trú\n( Đối với người Nước ngoài ) :',
            leftValue: viewBADetails.placeOfResidenceRegistration,
            rightLabel: 'Số giấy phép lao động\n( Đối với người Nước ngoài ) :',
            rightValue: viewBADetails.workPermitPhoneNumber
        },
        {
            leftLabel: 'Tên ngân hàng :',
            leftValue: viewBADetails.bankName,
            rightLabel: 'Số tài khoản ngân hàng :',
            rightValue: viewBADetails.bankAccountNumber
        },
        { leftLabel: 'Danh hiệu :', leftValue: getFullRank(viewBADetails.rank), rightLabel: 'Mã số người bảo trợ :', rightValue: viewBADetails.sponsorId },
        {
            leftLabel: 'Tên người bảo trợ :',
            leftValue: viewBADetails.nameOfSponsor,
            rightLabel: 'Danh hiệu người bảo trợ :',
            rightValue: getFullRank(viewBADetails.rankOfSponsor)
        }
    ]

    return (
        <PageContainer>
            <Typography variant="h6" fontWeight={700} color="text.primary" mb={3}>
                {currentBaid}, {currentName}
            </Typography>

            <Paper elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 2, overflow: 'hidden' }}>
                {rows.map((row, index) => (
                    <Stack key={index} direction={{ xs: 'column', md: 'row' }} sx={{ borderBottom: index < rows.length - 1 ? '1px solid #e2e8f0' : 'none' }}>
                        {/* Left side */}
                        <Stack direction={{ xs: 'column', sm: 'row' }} flex={1} sx={{ borderRight: { md: '1px solid #e2e8f0' } }}>
                            <Stack
                                justifyContent="center"
                                alignItems={{ xs: 'flex-start', sm: 'flex-end' }}
                                sx={{
                                    width: { xs: '100%', sm: 200 },
                                    minWidth: { sm: 200 },
                                    p: 1.5,
                                    backgroundColor: '#f8fafc',
                                    borderRight: { sm: '1px solid #e2e8f0' },
                                    borderBottom: { xs: '1px solid #e2e8f0', sm: 'none' }
                                }}>
                                <Typography variant="body2" fontWeight={600} color="text.primary" textAlign={{ xs: 'left', sm: 'right' }} whiteSpace="pre-line">
                                    {row.leftLabel}
                                </Typography>
                            </Stack>
                            <Stack justifyContent="center" flex={1} sx={{ p: 1.5, minHeight: 48 }}>
                                <Typography variant="body2" color="text.primary">
                                    {row.leftValue || '-'}
                                </Typography>
                            </Stack>
                        </Stack>

                        {/* Right side */}
                        <Stack direction={{ xs: 'column', sm: 'row' }} flex={1} sx={{ borderTop: { xs: '1px solid #e2e8f0', md: 'none' } }}>
                            <Stack
                                justifyContent="center"
                                alignItems={{ xs: 'flex-start', sm: 'flex-end' }}
                                sx={{
                                    width: { xs: '100%', sm: 200 },
                                    minWidth: { sm: 200 },
                                    p: 1.5,
                                    backgroundColor: '#f8fafc',
                                    borderRight: { sm: '1px solid #e2e8f0' },
                                    borderBottom: { xs: '1px solid #e2e8f0', sm: 'none' }
                                }}>
                                <Typography variant="body2" fontWeight={600} color="text.primary" textAlign={{ xs: 'left', sm: 'right' }} whiteSpace="pre-line">
                                    {row.rightLabel}
                                </Typography>
                            </Stack>
                            <Stack justifyContent="center" flex={1} sx={{ p: 1.5, minHeight: 48 }}>
                                <Typography variant="body2" color="text.primary">
                                    {row.rightValue || '-'}
                                </Typography>
                            </Stack>
                        </Stack>
                    </Stack>
                ))}
            </Paper>

            <Stack alignItems="center" mt={4}>
                <Button
                    variant="contained"
                    onClick={handleGoBack}
                    sx={{
                        backgroundColor: '#dc3545',
                        textTransform: 'capitalize',
                        px: 6,
                        py: 1,
                        fontSize: '1rem',
                        '&:hover': { backgroundColor: '#c82333' }
                    }}>
                    Trở lại
                </Button>
            </Stack>
        </PageContainer>
    )
}

export default ViewBADetailsLayout
